import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addAdminBrandLogo,
  createAdminBrand,
  getAdminBrands,
  removeAdminBrandLogo,
  updateAdminBrand,
  uploadAdminBrandLogo,
  type AdminBrand,
} from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";

const INPUT_CLASS = "w-full rounded-sm border border-line px-3 py-2 text-sm text-ink";

/**
 * `/admin/catalogue/marques` — équivalent de
 * apps/web/app/(admin)/admin/catalogue/marques/page.tsx : liste des marques +
 * création/édition inline. Pas de suppression à ce lot (inchangé de
 * l'original). Cf. AdminCatalogueCategoriesPage.tsx pour le détail de l'écart
 * `?error=`/`?success=` → état de mutation local.
 */
export function AdminCatalogueMarquesPage() {
  const queryClient = useQueryClient();
  const brandsQuery = useQuery({
    queryKey: ["admin", "brands"],
    queryFn: () => getAdminBrands(),
  });

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; message: string } | null>(null);
  const invalidateBrands = () => queryClient.invalidateQueries({ queryKey: ["admin", "brands"] });

  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof createAdminBrand>[1]) => createAdminBrand(undefined, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Marque enregistrée." });
      invalidateBrands();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateAdminBrand>[2] }) =>
      updateAdminBrand(undefined, id, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Marque enregistrée." });
      invalidateBrands();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const addLogoMutation = useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) => addAdminBrandLogo(undefined, id, url),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Logo ajouté." });
      invalidateBrands();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const uploadLogoMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadAdminBrandLogo(undefined, id, file),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Logo envoyé." });
      invalidateBrands();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const removeLogoMutation = useMutation({
    mutationFn: (id: string) => removeAdminBrandLogo(undefined, id),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Logo supprimé." });
      invalidateBrands();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  function handleAddLogoUrlSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const url = String(formData.get("logoUrl") ?? "").trim();
    if (!url) {
      setFeedback({ type: "error", message: "L'URL du logo est obligatoire." });
      return;
    }
    addLogoMutation.mutate({ id, url }, { onSuccess: () => formEl.reset() });
  }

  function handleUploadLogoSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const file = formData.get("logoFile");
    if (!(file instanceof File) || file.size === 0) {
      setFeedback({ type: "error", message: "Choisissez un fichier image à envoyer." });
      return;
    }
    uploadLogoMutation.mutate({ id, file }, { onSuccess: () => formEl.reset() });
  }

  function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const name = String(formData.get("name") ?? "").trim();
    if (!name) {
      setFeedback({ type: "error", message: "Le nom est obligatoire." });
      return;
    }
    const slug = String(formData.get("slug") ?? "").trim();
    const countryOfOrigin = String(formData.get("countryOfOrigin") ?? "").trim();
    const isFeatured = formData.get("isFeatured") === "on";

    createMutation.mutate(
      { name, slug: slug || undefined, countryOfOrigin: countryOfOrigin || undefined, isFeatured },
      { onSuccess: () => formEl.reset() },
    );
  }

  function handleUpdateSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formData = new FormData(event.currentTarget);
    updateMutation.mutate({
      id,
      input: {
        name: String(formData.get("name") ?? "").trim() || undefined,
        countryOfOrigin: String(formData.get("countryOfOrigin") ?? "").trim() || undefined,
        isFeatured: formData.get("isFeatured") === "on",
      },
    });
  }

  const brands = brandsQuery.data ?? [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="flex items-center gap-3">
        <Link to="/admin/catalogue" className="text-sm font-medium text-navy hover:underline">
          ← Catalogue
        </Link>
      </div>

      <h1 className="mt-2 font-display text-2xl font-semibold text-ink">Marques</h1>
      <p className="mt-1 text-sm text-muted">{brands.length} marque(s). Pas de suppression à ce lot.</p>

      {brandsQuery.isLoading && <p className="mt-4 text-sm text-muted">Chargement…</p>}
      {brandsQuery.isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(brandsQuery.error)}
        </p>
      )}

      {feedback && (
        <p
          className={[
            "mt-4 rounded-sm border px-4 py-3 text-sm",
            feedback.type === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-green-300 bg-green-50 text-green-700",
          ].join(" ")}
        >
          {feedback.message}
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="overflow-x-auto rounded-sm border border-line bg-white">
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3">Marque</th>
                <th className="px-4 py-3">Pays</th>
                <th className="px-4 py-3">Mise en avant</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                    Aucune marque pour le moment.
                  </td>
                </tr>
              )}
              {brands.map((brand) => (
                <BrandRow
                  key={brand.id}
                  brand={brand}
                  onSubmit={handleUpdateSubmit}
                  disabled={isPending}
                  onAddLogoUrl={handleAddLogoUrlSubmit}
                  onUploadLogo={handleUploadLogoSubmit}
                  onRemoveLogo={(id) => removeLogoMutation.mutate(id)}
                  addLogoPending={addLogoMutation.isPending}
                  uploadLogoPending={uploadLogoMutation.isPending}
                  removeLogoPending={removeLogoMutation.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-sm border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">Nouvelle marque</h2>
          <form onSubmit={handleCreateSubmit} className="mt-3 space-y-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-sm font-medium text-ink">
                Nom
              </label>
              <input id="name" name="name" type="text" required className={INPUT_CLASS} />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="slug" className="text-sm font-medium text-ink">
                Slug (optionnel)
              </label>
              <input id="slug" name="slug" type="text" className={INPUT_CLASS} />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="countryOfOrigin" className="text-sm font-medium text-ink">
                Pays d&apos;origine (code ISO 2, optionnel)
              </label>
              <input
                id="countryOfOrigin"
                name="countryOfOrigin"
                type="text"
                maxLength={2}
                placeholder="CI"
                className={INPUT_CLASS}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" name="isFeatured" />
              Mise en avant
            </label>

            <button
              type="submit"
              disabled={isPending}
              className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Créer
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function BrandRow({
  brand,
  onSubmit,
  disabled,
  onAddLogoUrl,
  onUploadLogo,
  onRemoveLogo,
  addLogoPending,
  uploadLogoPending,
  removeLogoPending,
}: {
  brand: AdminBrand;
  onSubmit: (event: FormEvent<HTMLFormElement>, id: string) => void;
  disabled: boolean;
  onAddLogoUrl: (event: FormEvent<HTMLFormElement>, id: string) => void;
  onUploadLogo: (event: FormEvent<HTMLFormElement>, id: string) => void;
  onRemoveLogo: (id: string) => void;
  addLogoPending: boolean;
  uploadLogoPending: boolean;
  removeLogoPending: boolean;
}) {
  return (
    <tr className="border-b border-line last:border-0 align-top">
      <td className="px-4 py-3">
        {brand.logoMedia ? (
          <img
            src={brand.logoMedia.url}
            alt={`Logo ${brand.name}`}
            className="mb-2 h-10 w-10 rounded-full border border-line object-cover"
          />
        ) : null}
        <p className="font-medium text-ink">{brand.name}</p>
        <p className="text-xs text-muted">/{brand.slug}</p>
      </td>
      <td className="px-4 py-3 text-ink">{brand.countryOfOrigin ?? "—"}</td>
      <td className="px-4 py-3 text-ink">{brand.isFeatured ? "Oui" : "Non"}</td>
      <td className="px-4 py-3">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-navy">Éditer</summary>
          <form onSubmit={(event) => onSubmit(event, brand.id)} className="mt-2 flex flex-wrap items-end gap-2">
            <input
              name="name"
              type="text"
              defaultValue={brand.name}
              className="w-36 rounded-sm border border-line px-2 py-1 text-sm text-ink"
            />
            <input
              name="countryOfOrigin"
              type="text"
              maxLength={2}
              defaultValue={brand.countryOfOrigin ?? ""}
              placeholder="Pays"
              className="w-16 rounded-sm border border-line px-2 py-1 text-sm text-ink"
            />
            <label className="flex items-center gap-1 pb-1 text-xs text-ink">
              <input type="checkbox" name="isFeatured" defaultChecked={brand.isFeatured} />
              En avant
            </label>
            <button
              type="submit"
              disabled={disabled}
              className="rounded-sm border border-navy px-2 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
            >
              OK
            </button>
          </form>

          <div className="mt-3 border-t border-line pt-3">
            <p className="text-xs font-medium text-muted">
              Logo (affiché en tuile circulaire "Marques à la une" et pastille côté vitrine).
            </p>

            {brand.logoMedia ? (
              <div className="relative mt-2 w-20">
                <img
                  src={brand.logoMedia.url}
                  alt={`Logo de ${brand.name}`}
                  className="h-20 w-20 rounded-full border border-line object-cover"
                />
                <button
                  type="button"
                  onClick={() => onRemoveLogo(brand.id)}
                  disabled={removeLogoPending}
                  title="Supprimer le logo"
                  aria-label="Supprimer le logo"
                  className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-xs font-bold text-red-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ×
                </button>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted">Aucun logo pour cette marque.</p>
            )}

            <form onSubmit={(event) => onAddLogoUrl(event, brand.id)} className="mt-3 flex flex-wrap items-end gap-2">
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor={`logoUrl-${brand.id}`} className="text-xs font-medium text-muted">
                  Ajouter/remplacer par une image (URL)
                </label>
                <input
                  id={`logoUrl-${brand.id}`}
                  name="logoUrl"
                  type="text"
                  placeholder="https://…"
                  className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
                />
              </div>
              <button
                type="submit"
                disabled={addLogoPending}
                className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
              >
                {addLogoPending ? "Ajout…" : "Ajouter"}
              </button>
            </form>

            <form
              onSubmit={(event) => onUploadLogo(event, brand.id)}
              className="mt-2 flex flex-wrap items-end gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor={`logoFile-${brand.id}`} className="text-xs font-medium text-muted">
                  Ou envoyer un fichier image (2 Mo max)
                </label>
                <input
                  id={`logoFile-${brand.id}`}
                  name="logoFile"
                  type="file"
                  accept="image/*"
                  className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
                />
              </div>
              <button
                type="submit"
                disabled={uploadLogoPending}
                className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploadLogoPending ? "Envoi…" : "Envoyer"}
              </button>
            </form>
          </div>
        </details>
      </td>
    </tr>
  );
}
